/**
 * Asks before unsaved edits are thrown away by leaving the page (f2.1).
 *
 * Two exits are covered:
 *
 * - **closing or reloading the tab**, and any full-page redirect such as
 *   signing out -- the browser's own `beforeunload` prompt;
 * - **following an in-app link** -- the sidebar, the breadcrumb, anything
 *   rendered as an anchor. The app uses a `BrowserRouter`, where React
 *   Router's `useBlocker` is unavailable, so the click is intercepted in the
 *   capture phase, before the router sees it, and cancelled if the Admin
 *   chooses to stay.
 *
 * Active only while there is something to lose, so a clean form costs nothing.
 */

import { useEffect } from 'react';

export function useUnsavedChangesGuard(active: boolean, message: string): void {
  useEffect(() => {
    if (!active) return undefined;

    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      // Required by some browsers to show the prompt; the text is ignored.
      event.returnValue = '';
    };

    const onClick = (event: MouseEvent) => {
      if (event.defaultPrevented || event.button !== 0) return;
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

      const target = event.target instanceof Element ? event.target : null;
      const anchor = target?.closest('a[href]');
      if (!(anchor instanceof HTMLAnchorElement)) return;
      if (anchor.target && anchor.target !== '_self') return;

      const destination = new URL(anchor.href, window.location.href);
      // Another origin is a full navigation, which `beforeunload` covers.
      if (destination.origin !== window.location.origin) return;
      if (destination.pathname === window.location.pathname) return;

      if (!window.confirm(message)) {
        event.preventDefault();
        event.stopPropagation();
      }
    };

    window.addEventListener('beforeunload', onBeforeUnload);
    document.addEventListener('click', onClick, true);
    return () => {
      window.removeEventListener('beforeunload', onBeforeUnload);
      document.removeEventListener('click', onClick, true);
    };
  }, [active, message]);
}
